// Canvasser terms for the Independent Contractor Agreement (Oct 26, 2026).
//
// A canvasser signs the same IC Agreement as everyone else, with three
// differences: the Restore Manager duties become Canvassing Services, the
// commission-based ending terms become fee-based ones, and the Commission
// Schedule is replaced by a Fee Schedule.
//
// The pay in the Fee Schedule must match what the CRM pays: finance_settings
// canvasser_hourly_rate, canvasser_app_rate_qualified,
// canvasser_app_rate_unqualified and canvasser_unqualified_carriers
// (fn_canvasser_app_rate, canvasser_payouts). Change both together.
//
// Used by the signing page (ICSigningPage.tsx) and the signed PDF
// (icAgreementPDF.ts), so the screen and the PDF always carry the same text.

export interface AgreementSection {
  heading: string;
  body: string;
}

export const CANVASSER_HOURLY_FEE = 35;
export const CANVASSER_APPOINTMENT_FEE = 10;
export const CANVASSER_UNQUALIFIED_APPOINTMENT_FEE = 5;

export function isCanvasserRole(role: string | null | undefined): boolean {
  return role === "canvasser";
}

const RECITALS: AgreementSection = {
  heading: "Recitals",
  body: `Company and IC desire to enter into a relationship in which either or both of them may disclose to the other certain trade secret, confidential or otherwise proprietary data or other information, and they each desire to ensure that such information is used only for the intended purposes.\n\nIn addition, Company and IC wish to clearly define their relationship and commitment to each other. The relationship will primarily revolve around marketing Company's roof inspection services to homeowners.`,
};

const SERVICES: AgreementSection = {
  heading: "Canvassing Services",
  body: `IC will provide door-to-door canvassing services that introduce Company's free roof inspection to homeowners. These services include: speaking with homeowners in neighborhoods where Company is seeking canvassing services; accurately recording each visit and its outcome in Company's canvassing app; asking each interested homeowner to name their homeowner's insurance company; and setting roof inspection appointments with interested homeowners, recorded in the app for Company to review.\n\nIC decides which days and hours to provide services and controls the manner and means of performing them. Company may share the areas where it is seeking canvassing services and the information it needs to accept an appointment, but does not set IC's hours.\n\nService time is documented by IC starting and ending a canvassing session in Company's canvassing app. While a session is active, the app records IC's location to document the time and visits being billed. Location is not recorded when no session is active. IC is responsible for starting and ending each session accurately.`,
};

const ENDING: AgreementSection = {
  heading: "Unpaid Fees and Customers",
  body: `1. False records. If IC records visits, service time, appointments, or insurance information that IC knows to be false, Company may withhold or reverse the fees tied to those records and may end this Agreement immediately.\n\n2. Customer non-contact. No matter why the relationship ends, IC gives up all rights to any homeowner for whom IC set an appointment while providing services to Company. IC agrees not to contact those homeowners to offer roofing or insurance restoration services for any other person or company for two (2) years after the relationship ends.\n\n3. Ending the relationship. Either party may end this Agreement at any time, for any reason, with written notice (a text message or email is enough). Company will pay all approved fees earned through the end date on its next regular contractor payment.`,
};

/** The Fee Schedule that replaces the Commission Schedule for canvassers. */
export const CANVASSER_FEE_SCHEDULE: AgreementSection[] = [
  {
    heading: "Hourly Service Fee",
    body: `$${CANVASSER_HOURLY_FEE}.00 per hour of documented canvassing time, measured from the start to the end of each canvassing session recorded in Company's canvassing app.`,
  },
  {
    heading: "Appointment Fee",
    body: `For each roof inspection appointment that IC sets and Company accepts:\n\n- $${CANVASSER_APPOINTMENT_FEE}.00 when the homeowner's insurance company is confirmed. An insurance company is confirmed when the homeowner names their current homeowner's insurance carrier and IC records it with the appointment.\n\n- $${CANVASSER_UNQUALIFIED_APPOINTMENT_FEE}.00 when the insurance company is not confirmed (for example, left blank, recorded as unknown, or the homeowner has no insurance), or when the homeowner's carrier is one Company has identified to IC in writing as an unqualified carrier. Nationwide and Liberty Mutual are currently unqualified carriers.`,
  },
  {
    heading: "No Commissions",
    body: `The fees in this Schedule are IC's entire compensation for the services. IC does not earn commissions, overrides, or any share of job revenue on appointments IC sets.`,
  },
  {
    heading: "Nature of Payments",
    body: `All fees are payments for services delivered and documented. They are not wages or salary, are not guaranteed, and do not include overtime, benefits, or tax withholding.`,
  },
  {
    heading: "Invoicing and Payment",
    body: `Company calculates IC's fees for each service week (Monday through Sunday) from the sessions and appointments recorded in the canvassing app, and that calculation serves as IC's invoice for the week. Fees are paid after Company's review on Company's regular contractor payment schedule.\n\nCompany will provide IC with IRS Form 1099-NEC as required by law. IC is solely responsible for timely payment of all federal and state income taxes on fees received.`,
  },
  {
    heading: "Adjustments",
    body: `Company may reverse an appointment fee on a later payment if the appointment is cancelled because it was not genuine, and may reduce it to the rate that applies if the insurance company recorded with it is found to be inaccurate. Company may correct calculation errors discovered within 90 days of payment, and may deduct the cost of unreturned Company property and any other amounts IC owes Company.`,
  },
];

/**
 * Turn the standard agreement sections into the canvasser version: swap the
 * Recitals, the Restore Manager duties and the commission-based ending terms,
 * and point every "Commission Schedule" mention at the Fee Schedule.
 */
export function canvasserAgreementSections(base: AgreementSection[]): AgreementSection[] {
  return base.map((s) => {
    if (s.heading === "Recitals") return RECITALS;
    if (s.heading === "Restore Manager Responsibilities") return SERVICES;
    if (s.heading === "Unpaid Commissions and Customers") return ENDING;
    return {
      heading: s.heading,
      body: s.body
        .replace(/final commission payments/g, "final payments")
        .replace(/Commission Schedule/g, "Fee Schedule"),
    };
  });
}
